// Sopwith 3D - scoring and end-of-level detection.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// Ported from SDL Sopwith swcollsn.c. Points go to the local player: gained
// for enemy losses, lost for friendly ones and for animals. The flight score
// fields feed the medals (M6).

import { FALLCOUNT, MAX_PLYR, MAXBOMBS, MAXFUEL, MAXROUNDS } from './constants';
import { clampMax, iabs } from './fixed';
import type { Game } from './game';
import { moveplyr, planeIsWounded } from './plane';
import { Faction, Obj, ObjState, ObjType, PlayMode, PowerupType, TargetType } from './types';

/** Which plane's score a loss of `ob` affects, and whether it is a penalty. */
function getScoreObject(g: Game, ob: Obj): { so: Obj; reverse: boolean } {
  if (g.playmode !== PlayMode.ASYNCH) {
    return {
      so: g.planes[0],
      reverse: ob.faction === Faction.PLAYER1 || ob.faction === Faction.NONE,
    };
  }
  return {
    so: g.planes[ob.faction === Faction.PLAYER1 ? 1 : 0],
    reverse: ob.faction === Faction.NONE,
  };
}

function scoretarg(g: Game, obp: Obj, score: number): void {
  const { so, reverse } = getScoreObject(g, obp);
  so.score.score += reverse ? -score : score;
}

function isHumanFaction(g: Game, f: Faction): boolean {
  for (let i = 0; i < MAX_PLYR; i++) {
    const p = g.planes[i];
    if (p && p.faction === f && p.movef === moveplyr) {
      return true;
    }
  }
  return false;
}

/** The human faction that has destroyed every other faction's targets, if any. */
function checkForWinner(g: Game): Faction {
  for (let i = Faction.PLAYER1; i < Faction.NUM_FACTIONS; i++) {
    if (!isHumanFaction(g, i)) {
      continue;
    }
    let j = Faction.PLAYER1;
    for (; j < Faction.NUM_FACTIONS; j++) {
      if (i !== j && g.numtarg[j] > 0) {
        break;
      }
    }
    if (j >= Faction.NUM_FACTIONS) {
      return i;
    }
  }
  return Faction.NONE;
}

/** How brave a kill was: far from home, low on fuel, or wounded. */
function computeValour(g: Game, ob: Obj): number {
  const { so, reverse } = getScoreObject(g, ob);
  if (reverse) {
    return 0;
  }
  const distance = iabs(so.originalOb!.x - so.x);
  let valour = distance < 500 ? 0 : ((distance - 500) / 350) | 0;
  const fuelfraction = ob.life > 0 ? (MAXFUEL / ob.life) | 0 : 1000;
  if (fuelfraction > 9) {
    valour++;
  }
  return planeIsWounded(so.state) ? (valour + 1) * 3 : valour * 2;
}

export function targetDestroyed(g: Game, ob: Obj, type: ObjType): void {
  const { so, reverse } = getScoreObject(g, ob);
  if (
    !reverse &&
    (type === ObjType.BOMB || type === ObjType.SHOT || type === ObjType.MISSILE || type === ObjType.PLANE)
  ) {
    so.flightscore.killscore += 4;
    so.flightscore.valour += 3 * computeValour(g, ob);
  }
  scoretarg(g, ob, ob.orient === TargetType.OIL_TANK ? 200 : 100);
  g.numtarg[ob.faction]--;
  const winner = checkForWinner(g);
  if (winner !== Faction.NONE) {
    g.endgame(winner);
  }
}

/** Penalty for killing an animal, only when a weapon or a flying plane did it. */
export function scorepenalty(g: Game, ttype: ObjType, obt: Obj | null, score: number): boolean {
  if (
    ttype === ObjType.SHOT ||
    ttype === ObjType.BOMB ||
    ttype === ObjType.MISSILE ||
    (ttype === ObjType.PLANE &&
      obt &&
      (obt.state === ObjState.FLYING ||
        obt.state === ObjState.WOUNDED ||
        (obt.state === ObjState.FALLING && obt.hitcount === FALLCOUNT)) &&
      !obt.athome)
  ) {
    scoretarg(g, obt!, score);
    return true;
  }
  return false;
}

export function scorepln(g: Game, ob: Obj, type: ObjType): void {
  const hadTakenOff = ob.life < MAXFUEL - MAXFUEL / 100;
  scoretarg(g, ob, 50);
  if (type === ObjType.BOMB || type === ObjType.SHOT || type === ObjType.MISSILE || type === ObjType.PLANE) {
    const { so, reverse } = getScoreObject(g, ob);
    if (!reverse) {
      if (hadTakenOff) {
        if (type !== ObjType.PLANE) {
          so.flightscore.planekills++;
        }
        so.flightscore.valour += 4 * (2 + computeValour(g, ob));
      }
      so.flightscore.killscore += 3;
    }
  }
}

export function powerupCollected(powerup: Obj, plane: Obj): void {
  powerup.state = ObjState.FINISHED;
  switch (powerup.orient) {
    case PowerupType.AMMO:
      plane.rounds = clampMax(plane.rounds + MAXROUNDS / 2, MAXROUNDS);
      break;
    case PowerupType.AMMO_BIG:
      plane.rounds = MAXROUNDS;
      break;
    case PowerupType.FUEL:
      plane.life = clampMax(plane.life + MAXFUEL / 2, MAXFUEL);
      break;
    case PowerupType.FUEL_BIG:
      plane.life = MAXFUEL;
      break;
    case PowerupType.BOMB:
      plane.bombs = clampMax(plane.bombs + ((MAXBOMBS / 2) | 0), MAXBOMBS);
      break;
    case PowerupType.BOMB_BIG:
      plane.bombs = MAXBOMBS;
      break;
    default:
      break;
  }
}
